import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  Search,
  CheckCircle,
  Close,
  Refresh,
  Security,
  PersonSearch,
  Warning,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { apiGet } from '../services/apiClient';
import {
  screeningService,
  ScreeningResult,
  ScreeningStats,
} from '../services/screeningService';
import { LoggerService } from '../services/LoggerService';

interface Investor {
  id: string;
  name: string;
  investor_type: string;
  email: string;
  status: string;
}

const AMLScreeningPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Stats
  const [stats, setStats] = useState<ScreeningStats | null>(null);

  // Investor search + screening
  const [investors, setInvestors] = useState<Investor[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [screeningInProgress, setScreeningInProgress] = useState<string | null>(null);

  // Results
  const [results, setResults] = useState<ScreeningResult[]>([]);
  const [resultsLoading, setResultsLoading] = useState(false);

  // Match detail dialog
  const [matchDialogOpen, setMatchDialogOpen] = useState(false);
  const [selectedResult, setSelectedResult] = useState<ScreeningResult | null>(null);
  const [resolveMatchId, setResolveMatchId] = useState<string | null>(null);
  const [resolution, setResolution] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [resolveLoading, setResolveLoading] = useState(false);

  // Pending rescreening
  const [pendingRescreening, setPendingRescreening] = useState<ScreeningResult[]>([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [bulkRescreenLoading, setBulkRescreenLoading] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const data = await screeningService.getStats();
      setStats(data);
    } catch (err: any) {
      LoggerService.error('Failed to load screening stats', err);
    }
  }, []);

  const loadInvestors = useCallback(async () => {
    try {
      const data = await apiGet<Investor[]>('/investors/');
      setInvestors(data);
    } catch (err: any) {
      LoggerService.error('Failed to load investors', err);
      setError(err.message || 'Failed to load investors');
    }
  }, []);

  const loadResults = useCallback(async () => {
    setResultsLoading(true);
    try {
      const data = await screeningService.getResults();
      setResults(data);
    } catch (err: any) {
      LoggerService.error('Failed to load screening results', err);
      enqueueSnackbar('Failed to load screening results', { variant: 'error' });
    } finally {
      setResultsLoading(false);
    }
  }, [enqueueSnackbar]);

  const loadPendingRescreening = useCallback(async () => {
    setPendingLoading(true);
    try {
      const data = await screeningService.getPendingRescreening();
      setPendingRescreening(data);
    } catch (err: any) {
      LoggerService.error('Failed to load pending rescreening', err);
    } finally {
      setPendingLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadStats(), loadInvestors(), loadResults(), loadPendingRescreening()]);
      setLoading(false);
    };
    init();
  }, [loadStats, loadInvestors, loadResults, loadPendingRescreening]);

  const handleScreenInvestor = async (investorId: string) => {
    setScreeningInProgress(investorId);
    try {
      const result = await screeningService.screenInvestor(investorId);
      setResults((prev) => [result, ...prev.filter((r) => r.investor_id !== investorId)]);
      await loadStats();
      if (result.match_count > 0) {
        enqueueSnackbar(`Screening complete: ${result.match_count} potential match(es) found`, { variant: 'warning' });
      } else {
        enqueueSnackbar('Screening complete: No matches found', { variant: 'success' });
      }
    } catch (err: any) {
      LoggerService.error('Screening failed', err);
      enqueueSnackbar(err.message || 'Screening failed', { variant: 'error' });
    } finally {
      setScreeningInProgress(null);
    }
  };

  const handleViewMatches = (result: ScreeningResult) => {
    setSelectedResult(result);
    setMatchDialogOpen(true);
  };

  const handleResolveMatch = async () => {
    if (!resolveMatchId || !resolution) return;
    setResolveLoading(true);
    try {
      const updatedMatch = await screeningService.resolveMatch(resolveMatchId, resolution, resolutionNotes);
      if (selectedResult) {
        const updatedMatches = selectedResult.matches.map((m) =>
          m.id === resolveMatchId ? updatedMatch : m,
        );
        setSelectedResult({ ...selectedResult, matches: updatedMatches });
      }
      await Promise.all([loadResults(), loadStats()]);
      enqueueSnackbar('Match resolved', { variant: 'success' });
      setResolveMatchId(null);
      setResolution('');
      setResolutionNotes('');
    } catch (err: any) {
      LoggerService.error('Failed to resolve match', err);
      enqueueSnackbar(err.message || 'Failed to resolve match', { variant: 'error' });
    } finally {
      setResolveLoading(false);
    }
  };

  const handleBulkRescreen = async () => {
    if (pendingRescreening.length === 0) return;
    setBulkRescreenLoading(true);
    try {
      const investorIds = pendingRescreening.map((r) => r.investor_id);
      const updatedResults = await screeningService.bulkRescreen(investorIds);
      setResults((prev) => {
        const existingIds = new Set(updatedResults.map((r) => r.investor_id));
        return [...updatedResults, ...prev.filter((r) => !existingIds.has(r.investor_id))];
      });
      await Promise.all([loadStats(), loadPendingRescreening()]);
      enqueueSnackbar(`Bulk rescreening complete for ${investorIds.length} investor(s)`, { variant: 'success' });
    } catch (err: any) {
      LoggerService.error('Bulk rescreening failed', err);
      enqueueSnackbar(err.message || 'Bulk rescreening failed', { variant: 'error' });
    } finally {
      setBulkRescreenLoading(false);
    }
  };

  const filteredInvestors = searchTerm
    ? investors.filter(
        (inv) =>
          inv.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          inv.email.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : investors;

  const getScoreColor = (score: number): string => {
    if (score >= 0.8) return '#ef4444';
    if (score >= 0.6) return '#f97316';
    if (score >= 0.4) return '#eab308';
    return '#22c55e';
  };

  const getStatusChip = (status: string) => {
    const map: Record<string, { color: 'default' | 'success' | 'warning' | 'error' | 'info'; label: string }> = {
      clear: { color: 'success', label: 'Clear' },
      pending_review: { color: 'warning', label: 'Pending Review' },
      confirmed_match: { color: 'error', label: 'Confirmed Match' },
      false_positive: { color: 'info', label: 'False Positive' },
      resolved: { color: 'success', label: 'Resolved' },
      pending: { color: 'default', label: 'Pending' },
    };
    const info = map[status] || { color: 'default' as const, label: status };
    return <Chip size="small" label={info.label} color={info.color} />;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={<Button onClick={() => { setError(null); loadInvestors(); }}>Retry</Button>}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>AML Screening</Typography>
        <Typography variant="body2" color="text.secondary">
          Anti-Money Laundering investor screening against sanctions and PEP lists
        </Typography>
      </Box>

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {stats.total_screened}
                </Typography>
                <Typography variant="body2" color="text.secondary">Total Screened</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main' }}>
                  {stats.matches_found}
                </Typography>
                <Typography variant="body2" color="text.secondary">Matches Found</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'info.main' }}>
                  {stats.false_positives}
                </Typography>
                <Typography variant="body2" color="text.secondary">False Positives</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'error.main' }}>
                  {stats.pending_review}
                </Typography>
                <Typography variant="body2" color="text.secondary">Pending Review</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Screen Investor */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            <PersonSearch sx={{ mr: 1, verticalAlign: 'middle' }} />
            Screen Investor
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Search investor by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{ mb: 2 }}
          />
          {searchTerm && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredInvestors.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ textAlign: 'center', py: 2 }}>
                        <Typography color="text.secondary">No investors match your search</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredInvestors.slice(0, 10).map((inv) => (
                      <TableRow key={inv.id} hover>
                        <TableCell>{inv.name}</TableCell>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{inv.investor_type}</TableCell>
                        <TableCell>{inv.email}</TableCell>
                        <TableCell>
                          <Chip size="small" label={inv.status} color={inv.status === 'active' ? 'success' : 'default'} />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={screeningInProgress === inv.id ? <CircularProgress size={14} /> : <Security />}
                            onClick={() => handleScreenInvestor(inv.id)}
                            disabled={screeningInProgress === inv.id}
                          >
                            Screen
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Screening Results */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              <Security sx={{ mr: 1, verticalAlign: 'middle' }} />
              Screening Results
            </Typography>
            <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={loadResults} disabled={resultsLoading}>
              Refresh
            </Button>
          </Box>

          {resultsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : results.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No screening results yet. Search and screen an investor to get started.
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Investor</TableCell>
                    <TableCell>Screening Date</TableCell>
                    <TableCell align="center">Matches</TableCell>
                    <TableCell align="center">Highest Score</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map((result) => (
                    <TableRow key={result.id} hover>
                      <TableCell>{result.investor_name}</TableCell>
                      <TableCell>{new Date(result.screening_date).toLocaleDateString()}</TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={result.match_count}
                          color={result.match_count > 0 ? 'warning' : 'success'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {result.highest_score > 0 ? (
                          <Chip
                            size="small"
                            label={`${(result.highest_score * 100).toFixed(0)}%`}
                            sx={{
                              backgroundColor: getScoreColor(result.highest_score),
                              color: '#fff',
                            }}
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>{getStatusChip(result.status)}</TableCell>
                      <TableCell align="right">
                        {result.match_count > 0 && (
                          <Button size="small" onClick={() => handleViewMatches(result)}>
                            View Matches
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Pending Rescreening */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              <Warning sx={{ mr: 1, verticalAlign: 'middle', color: 'warning.main' }} />
              Pending Rescreening ({pendingRescreening.length})
            </Typography>
            <Button
              variant="contained"
              color="warning"
              startIcon={bulkRescreenLoading ? <CircularProgress size={16} /> : <Refresh />}
              onClick={handleBulkRescreen}
              disabled={bulkRescreenLoading || pendingRescreening.length === 0}
            >
              Bulk Rescreen All
            </Button>
          </Box>

          {pendingLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : pendingRescreening.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No investors pending rescreening
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Investor</TableCell>
                    <TableCell>Last Screening</TableCell>
                    <TableCell>Next Due</TableCell>
                    <TableCell>Previous Status</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingRescreening.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{item.investor_name}</TableCell>
                      <TableCell>{new Date(item.screening_date).toLocaleDateString()}</TableCell>
                      <TableCell>
                        {item.next_rescreening_date
                          ? new Date(item.next_rescreening_date).toLocaleDateString()
                          : 'Overdue'}
                      </TableCell>
                      <TableCell>{getStatusChip(item.status)}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={screeningInProgress === item.investor_id ? <CircularProgress size={14} /> : <Security />}
                          onClick={() => handleScreenInvestor(item.investor_id)}
                          disabled={screeningInProgress === item.investor_id}
                        >
                          Rescreen
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Match Detail Dialog */}
      <Dialog open={matchDialogOpen} onClose={() => { setMatchDialogOpen(false); setResolveMatchId(null); }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Screening Matches — {selectedResult?.investor_name}
          </Typography>
          <IconButton onClick={() => { setMatchDialogOpen(false); setResolveMatchId(null); }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedResult?.matches && selectedResult.matches.length > 0 ? (
            selectedResult.matches.map((match) => (
              <Paper key={match.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">Matched Entity</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{match.matched_entity_name}</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">Entity Type</Typography>
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{match.matched_entity_type}</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">Match Score</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={match.match_score * 100}
                        sx={{
                          flex: 1,
                          height: 8,
                          borderRadius: 4,
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: getScoreColor(match.match_score),
                          },
                        }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 600, color: getScoreColor(match.match_score) }}>
                        {(match.match_score * 100).toFixed(0)}%
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <Typography variant="subtitle2" color="text.secondary">Source</Typography>
                    <Typography variant="body2">{match.source}</Typography>
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                    {getStatusChip(match.status)}
                  </Grid>
                  {match.resolved_by && (
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">Resolved By</Typography>
                      <Typography variant="body2">
                        {match.resolved_by} on {match.resolved_at ? new Date(match.resolved_at).toLocaleDateString() : '-'}
                      </Typography>
                    </Grid>
                  )}
                  {match.resolution_notes && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">Resolution Notes</Typography>
                      <Typography variant="body2">{match.resolution_notes}</Typography>
                    </Grid>
                  )}
                  {match.match_details && Object.keys(match.match_details).length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">Match Details</Typography>
                      <Paper variant="outlined" sx={{ p: 1, mt: 0.5, bgcolor: 'action.hover' }}>
                        {Object.entries(match.match_details).map(([key, value]) => (
                          <Typography key={key} variant="body2">
                            <strong>{key}:</strong> {String(value)}
                          </Typography>
                        ))}
                      </Paper>
                    </Grid>
                  )}

                  {/* Resolve controls */}
                  {match.status === 'pending' || match.status === 'pending_review' ? (
                    resolveMatchId === match.id ? (
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={4}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Resolution</InputLabel>
                              <Select value={resolution} onChange={(e) => setResolution(e.target.value)} label="Resolution">
                                <MenuItem value="false_positive">False Positive</MenuItem>
                                <MenuItem value="confirmed_match">Confirmed Match</MenuItem>
                                <MenuItem value="escalated">Escalated</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={5}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Notes"
                              value={resolutionNotes}
                              onChange={(e) => setResolutionNotes(e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={handleResolveMatch}
                                disabled={!resolution || resolveLoading}
                                startIcon={resolveLoading ? <CircularProgress size={14} /> : <CheckCircle />}
                              >
                                Submit
                              </Button>
                              <Button size="small" onClick={() => { setResolveMatchId(null); setResolution(''); setResolutionNotes(''); }}>
                                Cancel
                              </Button>
                            </Box>
                          </Grid>
                        </Grid>
                      </Grid>
                    ) : (
                      <Grid item xs={12}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setResolveMatchId(match.id)}
                        >
                          Resolve
                        </Button>
                      </Grid>
                    )
                  ) : null}
                </Grid>
              </Paper>
            ))
          ) : (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No match details available
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setMatchDialogOpen(false); setResolveMatchId(null); }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AMLScreeningPage;
